import type { BlowoutWeek } from "@averagejoesff/shared";
import { TeamManagerLabel } from "../../components/TeamManagerLabel";
import { formatMargin, formatPoints } from "../../lib/format";

interface BlowoutWeeklyTableProps {
  weeks: BlowoutWeek[];
  /** Team name -> manager label to show alongside it (see `useManagerNames`). */
  labelByTeam: Map<string, string>;
}

/** A week with a recorded result — narrows winner/loser/margin to non-null. */
type PlayedWeek = BlowoutWeek & {
  winner: NonNullable<BlowoutWeek["winner"]>;
  loser: NonNullable<BlowoutWeek["loser"]>;
  margin: number;
};

function hasResult(week: BlowoutWeek): week is PlayedWeek {
  return (
    week.status === "final" &&
    week.winner != null &&
    week.loser != null &&
    week.margin !== null
  );
}

export function BlowoutWeeklyTable({ weeks, labelByTeam }: BlowoutWeeklyTableProps) {
  // Only show weeks that have finished — in-progress and unplayed weeks
  // just clutter the table until a final result exists.
  const playedWeeks = weeks.filter(hasResult);

  if (playedWeeks.length === 0) {
    return <p className="blowout-table__empty">No weeks played yet.</p>;
  }

  return (
    <div className="table-scroll">
      <table className="blowout-table">
        <caption className="blowout-table__caption">
          Weekly Winners - Largest margin of victory each week
        </caption>
        <thead>
          <tr>
            <th scope="col">Wk</th>
            <th scope="col">Winner</th>
            <th scope="col" className="num">
              Score
            </th>
            <th scope="col">Beat</th>
            <th scope="col" className="num">
              Score
            </th>
            <th scope="col" className="num">
              Margin
            </th>
          </tr>
        </thead>
        <tbody>
          {playedWeeks.map((week) => (
            <WeekRow key={week.week} week={week} labelByTeam={labelByTeam} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WeekRow({ week, labelByTeam }: { week: PlayedWeek; labelByTeam: Map<string, string> }) {
  return (
    <tr className="blowout-table__row">
      <th scope="row">{week.week}</th>
      <td data-label="Winner" aria-label={`Winner: ${week.winner.name}`}>
        <span className="team-name">{week.winner.name}</span>
        <TeamManagerLabel label={labelByTeam.get(week.winner.name)} />
      </td>
      <td
        className="num"
        data-label="Winner score"
        aria-label={`Winner score: ${formatPoints(week.winner.points)}`}
      >
        {formatPoints(week.winner.points)}
      </td>
      <td data-label="Beat" aria-label={`Beat: ${week.loser.name}`}>
        <span className="team-name team-name--muted">{week.loser.name}</span>
        <TeamManagerLabel label={labelByTeam.get(week.loser.name)} />
      </td>
      <td
        className="num"
        data-label="Their score"
        aria-label={`Their score: ${formatPoints(week.loser.points)}`}
      >
        {formatPoints(week.loser.points)}
      </td>
      <td
        className="num margin"
        data-label="Margin"
        aria-label={`Margin: ${formatMargin(week.margin)}`}
      >
        {formatMargin(week.margin)}
      </td>
    </tr>
  );
}
